import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import { tourLead } from '../lib/api';
import { useApp } from '../context/AppContext';
import { toast } from 'sonner';
import { ArrowRight, ArrowLeft, Music, Headphones, Film, Check, Sparkles, Radio, GraduationCap, Library, Bot, Bookmark, Loader2, Flame, Play, Pause, Star, DollarSign, Shield, Send, Rocket, Calendar, ChevronRight, Eye, Zap, MessageSquare } from 'lucide-react';
import PwaInstallButton from '../components/PwaInstallButton';

const PATHS = [
  { id: 'artist',   label: 'Artist / Musician', icon: Music,      color: '#a78bfa', blurb: 'Release music, learn the business, get AI coaching.' },
  { id: 'creator',  label: 'Video Creator',     icon: Film,       color: '#ff7a00', blurb: 'Host, stream, and monetize video content.' },
  { id: 'listener', label: 'Listener / Fan',    icon: Headphones, color: '#22d3ee', blurb: 'Discover music, read, listen, follow artists.' },
];

const INTERESTS_BY_PATH = {
  artist:   ['Releasing music', 'Music publishing', 'Sync licensing', 'Building a fan base', 'Royalty collection', 'Live performance', 'AI-assisted marketing'],
  creator:  ['Music videos', 'Vlogs / daily content', 'Educational courses', 'Tutorials', 'Live streaming', 'Ad monetization'],
  listener: ['Indie rock', 'Hip-hop', 'Electronic', 'Jazz & soul', 'Country / folk', 'World music', 'Podcasts & audiobooks'],
};

// Hard facts that build desire (stats, not hype)
const STAKES_BY_PATH = {
  artist: [
    { big: '82%', small: 'of indie musicians are missing one or more royalty income streams. On average they leave $340+/year per active song uncollected.' },
    { big: '7 tracks', small: 'in the Music Biz Academy — copyright, distribution, royalties, publishing, performance rights, publishing company, sync licensing.' },
    { big: '7 AI specialists', small: 'unlocked by learning — your personal Publisher Agent, Royalty Auditor, Licensing Advisor, and more.' },
  ],
  creator: [
    { big: '$0', small: 'Free video hosting. No pay-per-GB math. Upgrade only when you want branded storefront + higher quality.' },
    { big: '4 formats', small: 'Turn one course into an ebook, audiobook, workbook, AND podcast — all in the Knowledge Hub.' },
    { big: '60-40', small: 'Creator-favorable split on ad revenue and Hub sales.' },
  ],
  listener: [
    { big: '24/7', small: 'Indie music radio from working artists — no algorithm, no ads on free tier, real humans on the decks.' },
    { big: '38 lessons', small: 'Audit the Music Biz Academy for free. Learn how the artists you listen to actually make their money.' },
    { big: 'Every dollar', small: 'paid in the Hub goes 70% to the publishing artist. Buy their ebook, they eat.' },
  ],
};

// Sample "first lesson" preview text for each path
const LESSON_PREVIEW = {
  artist: {
    title: 'Master vs Composition — the two copyrights every song has',
    body: "Every commercially released song has TWO separate copyrights, and confusing them costs musicians money every day.\n\nThe Composition is the melody, lyrics, and underlying musical work — owned by the songwriter(s). Generates songwriter royalties and publisher royalties.\n\nThe Master is the specific recorded performance — owned by whoever paid for the recording: usually the artist (if self-funded) or the label. Generates recording royalties (streams, sales, neighbouring rights).\n\nExample: if you cover a Beatles song, the Beatles' estate and Sony/ATV own the composition forever — they get paid every stream. You own the master of YOUR cover.\n\nThis is why Taylor Swift re-recorded her old catalog…",
  },
  creator: {
    title: 'Monetization 101 — five ways your videos make money on ReaperCast',
    body: "You've been told there's only one way to make money from video: ads. Here's the real list.\n\n1. Ads — pre-roll + mid-roll via our VAST pipeline or AdSense. ~$2-5 per 1000 views for general content, $8-25 for niche education.\n\n2. Tips — viewers tip $1-100 during live streams (Stripe, creator takes 85%).\n\n3. Memberships — $5-50/mo subscribers get exclusive content.\n\n4. Ebook/course cross-sell — publish companion Hub content, link from video description.\n\n5. Affiliate — tracked partner links with honest disclosure. Niche creators can earn more here than ads.\n\nStack all five and videos earning <$500/mo on YouTube-only can clear $3-5k/mo here…",
  },
  listener: {
    title: "Why the music you love stays underground (and what you can do about it)",
    body: "Here's a hard truth: most of the music you'd love to hear never reaches you because distribution is pay-to-play.\n\nSpotify editorial playlists are pitched by major labels with PR budgets your favorite indie artist can't match. The algorithm 'discovery' features you see are trained on what other people already stream — meaning new artists never break through by luck.\n\nReaperCast Radio is human-curated. Artists upload, a real human queues the rotation, listeners like you tune in. No pay-for-placement. No algorithmic bias. You hear what's actually good, and the artist gets paid fair performance royalties.\n\nYou can also directly support via the Hub…",
  },
};

// Canned specialist conversation preview — deterministic & free (no API call)
const AGENT_PREVIEW = {
  artist: {
    agent: 'Publisher Agent',
    agent_emoji: '📜',
    user_q: "I've released 12 songs but never registered them anywhere. What do I do first?",
    reply: "Register with ONE performing rights org first — pick ASCAP (free songwriter membership) or BMI (free songwriter, $150 publisher fee). Do NOT join more than one.\n\nThen register every song in your catalog — it takes about 5 minutes per song. Without this, you're leaving performance royalties unclaimed in the PRO's house account.\n\nNext: register with the MLC at mlc.co for US streaming mechanicals. Free. Also instant.\n\nThis alone recovers the majority of your missing royalty income. Do it this week. Ask me your next question when you're registered.",
  },
  creator: {
    agent: 'Release Strategist',
    agent_emoji: '🚀',
    user_q: "I'm releasing a music video Friday. What should I actually do this week?",
    reply: "Too late for Friday — real talk. You need 7+ days minimum for Spotify/Apple editorial pitches. But here's your 7-day sprint for a good result:\n\nMon: Finalize thumbnail (3 variants). Upload unlisted to VidCreForge. Prep smart link via Feature.fm.\n\nTue: Post 15-sec teaser clips on TikTok, Reels, Shorts. Use the HOOK not the verse.\n\nWed: Email list blast + DM your 50 most-engaged fans personally.\n\nThu: Schedule push notifications. Enable Content ID.\n\nFri 00:00 local: Go live. Pin everywhere. Reply to every comment/share within 24hr.\n\nSat-Sun: Second teaser wave + thank-you posts.\n\nNext time, start 6 weeks out.",
  },
  listener: {
    agent: 'Discovery Bot',
    agent_emoji: '🎧',
    user_q: "I like The War on Drugs and Big Thief. Who's on ReaperCast I should try?",
    reply: "Two lanes stand out in your taste — dreamy rock with texture, and lyrical indie folk with muscle.\n\nQueue in this order: Softer Skies (Seattle-based, similar hazy build), Lonesome Atlas (Philly indie, heavier Big Thief energy), and Nash Calder (Tennessee roots folk with shoegaze edges).\n\nAll three have full catalogs on ReaperCast Radio tonight + are in Hub audiobook rotation if you want their 'making of' stories.\n\nWant me to queue them up in order?",
  },
};

// 30-day personalized roadmap
const ROADMAP_BY_PATH = {
  artist: [
    { day: 'Day 1',  what: 'Upload your first track → it airs on ReaperCast Radio within the hour.' },
    { day: 'Day 3',  what: 'Finish the Copyright Foundations track → unlock the Rights Coach agent.' },
    { day: 'Day 7',  what: 'Distribute to Spotify/Apple via our partner distributors — affiliate links save you the middleman fees.' },
    { day: 'Day 14', what: 'Complete Royalty Types track → the Royalty Auditor will audit your distributor statements.' },
    { day: 'Day 30', what: 'Publish your first ebook/podcast in the Hub → educator income starts.' },
  ],
  creator: [
    { day: 'Day 1',  what: 'Upload your first video. Pick a storefront theme in 2 clicks.' },
    { day: 'Day 7',  what: 'Enable monetization — tips, ads, memberships — from your dashboard.' },
    { day: 'Day 14', what: 'Publish your first course to the Knowledge Hub (auto-converts to ebook + audiobook).' },
    { day: 'Day 30', what: 'First payout scheduled — creator-favorable 60/40 split.' },
  ],
  listener: [
    { day: 'Day 1',  what: 'Tune into ReaperCast Radio and save your first 10 tracks.' },
    { day: 'Day 3',  what: 'Follow 5 artists whose catalog resonates.' },
    { day: 'Day 7',  what: 'Subscribe to 1-2 podcasts in the Hub.' },
    { day: 'Day 30', what: 'Your personalized Discovery queue delivers 3 new artists per week.' },
  ],
};

export default function Tour() {
  const nav = useNavigate();
  const { signup } = useApp();
  const [step, setStep] = useState(0);
  const [path, setPath] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [interests, setInterests] = useState([]);
  const [pwd, setPwd] = useState('');
  const [busy, setBusy] = useState(false);

  const pingLead = async (patch = {}) => {
    try {
      const payload = { path, name, email: email || undefined, interests, step_reached: step, completed: false, ...patch };
      if (!payload.email) delete payload.email;
      await tourLead(payload);
    } catch {}
  };

  const totalSteps = 9;
  const progress = Math.round(((step + 1) / totalSteps) * 100);
  const color = PATHS.find(p => p.id === path)?.color || '#a78bfa';
  const stakes = STAKES_BY_PATH[path] || STAKES_BY_PATH.artist;
  const lesson = LESSON_PREVIEW[path] || LESSON_PREVIEW.artist;
  const agent = AGENT_PREVIEW[path] || AGENT_PREVIEW.artist;
  const roadmap = ROADMAP_BY_PATH[path] || ROADMAP_BY_PATH.artist;
  const availableInterests = useMemo(() => INTERESTS_BY_PATH[path] || INTERESTS_BY_PATH.artist, [path]);

  const next = () => { setStep(s => Math.min(totalSteps - 1, s + 1)); pingLead({ step_reached: step + 1 }); };
  const back = () => setStep(s => Math.max(0, s - 1));

  const finishWithAccount = async () => {
    if (!email.trim() || !pwd.trim()) { toast.error('Email and password required'); return; }
    setBusy(true);
    try {
      const profile_type = path === 'artist' ? 'musician' : (path === 'listener' ? 'listener' : 'creator');
      const extras = { profile_type };
      if (path === 'artist') extras.reapercast_optin = true;
      await signup(email.trim(), pwd, name.trim() || undefined, extras);
      await tourLead({ path, name, email, interests, step_reached: step, completed: true });
      toast.success('Welcome aboard!');
      nav(path === 'artist' ? '/reapercast' : (path === 'listener' ? '/hub' : '/home'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Signup failed');
    } finally { setBusy(false); }
  };

  const finishAsLead = async () => {
    if (!email.trim()) { toast.error('Drop an email so we can save your spot'); return; }
    setBusy(true);
    try {
      await tourLead({ path, name, email, interests, step_reached: step, completed: true });
      toast.success('Saved — check your inbox for the signup link.');
      nav('/');
    } catch { toast.error('Failed to save'); }
    finally { setBusy(false); }
  };

  const Header = (
    <div className="flex items-center justify-between mb-8">
      <Link to="/" className="flex items-center gap-2"><Logo /></Link>
      <div className="text-[12px] text-white/55">Step {step + 1} / {totalSteps}</div>
    </div>
  );

  const ProgressBar = (
    <div className="h-1 bg-white/10 rounded-full overflow-hidden mb-10" aria-hidden>
      <div className="h-full transition-all duration-500" style={{ width: progress + '%', background: `linear-gradient(90deg,${color},#22d3ee)` }} />
    </div>
  );

  return (
    <div data-testid="tour-page" className="min-h-screen bg-[#0a0a0c] text-white">
      <div className="max-w-[960px] mx-auto px-5 py-8">
        {Header}
        {ProgressBar}

        {step === 0 && (
          <div data-testid="tour-step-welcome">
            <div className="chip"><span className="live-dot" /> Interactive tour · no signup to explore</div>
            <h1 className="font-display text-[44px] md:text-[68px] mt-3 leading-[1.02]">Welcome to <span className="brand-gradient">ReaperCast</span>.</h1>
            <p className="text-white/70 text-[16px] mt-4 max-w-[640px] leading-relaxed">Before we ask for anything, let's show you what's actually in here. Pick your lane — we'll tailor the tour.</p>
            <div className="mt-8 grid sm:grid-cols-3 gap-3">
              {PATHS.map(p => (
                <button key={p.id} onClick={() => { setPath(p.id); setStep(1); pingLead({ path: p.id, step_reached: 1 }); }}
                  className="card-shell p-5 text-left hover-lift" style={{ borderColor: p.color + '55' }} data-testid={`tour-path-${p.id}`}>
                  <div className="w-11 h-11 rounded-xl grid place-items-center" style={{ background: p.color + '22', color: p.color }}><p.icon className="w-5 h-5" /></div>
                  <div className="font-display text-[19px] mt-3">{p.label}</div>
                  <div className="text-[13px] text-white/65 mt-1">{p.blurb}</div>
                  <div className="mt-3 text-[12.5px] inline-flex items-center gap-1" style={{ color: p.color }}>Start tour <ArrowRight className="w-3.5 h-3.5" /></div>
                </button>
              ))}
            </div>
            <div className="text-[12.5px] text-white/45 mt-6 text-center">Already have an account? <Link to="/login" className="text-[#22d3ee] hover:underline">Sign in</Link></div>
          </div>
        )}

        {step === 1 && (
          <div data-testid="tour-step-name">
            <div className="chip" style={{ color }}>Quick intro</div>
            <h2 className="font-display text-[36px] md:text-[46px] mt-3">What should we call you?</h2>
            <p className="text-white/60 mt-2 text-[15px]">So we can personalize the rest of the tour. No account needed yet.</p>
            <input autoFocus value={name} onChange={e => setName(e.target.value)} placeholder="Your artist or display name" className="input-field mt-6 max-w-[460px] text-[16px]" data-testid="tour-name-input" />
            <div className="flex gap-2 mt-6">
              <button onClick={back} className="btn-ghost"><ArrowLeft className="w-4 h-4" /> Back</button>
              <button onClick={next} disabled={!name.trim()} className="btn-primary disabled:opacity-50" data-testid="tour-next-name">Continue <ArrowRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="tour-step-stakes">
            <div className="chip" style={{ color }}>{name ? `${name}, ` : ''}here's what's at stake</div>
            <h2 className="font-display text-[36px] md:text-[48px] mt-3">The honest numbers.</h2>
            <p className="text-white/65 mt-2 text-[14.5px] max-w-[620px]">We don't sell hype. Here's why this matters before you spend any more time here.</p>
            <div className="grid sm:grid-cols-3 gap-3 mt-7">
              {stakes.map((s, i) => (
                <div key={i} className="card-shell p-5 hover-lift">
                  <div className="font-display text-[38px] brand-gradient leading-none">{s.big}</div>
                  <div className="text-[13px] text-white/70 mt-3 leading-relaxed">{s.small}</div>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-8">
              <button onClick={back} className="btn-ghost"><ArrowLeft className="w-4 h-4" /> Back</button>
              <button onClick={next} className="btn-primary" data-testid="tour-next-stakes">OK, show me <ArrowRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div data-testid="tour-step-lesson">
            <div className="chip" style={{ color }}><GraduationCap className="w-3.5 h-3.5 mr-1 inline" /> Sample Academy lesson</div>
            <h2 className="font-display text-[32px] md:text-[40px] mt-3">Read something real, right now.</h2>
            <p className="text-white/65 mt-1 text-[14px]">Here's a preview from the Academy — no signup, no paywall. This is the kind of content you'd read on day 1.</p>
            <article className="card-shell p-6 md:p-7 mt-6">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-white/50"><Eye className="w-3 h-3" /> Preview · 3 min read</div>
              <h3 className="font-display text-[24px] mt-2">{lesson.title}</h3>
              <p className="mt-3 text-[14.5px] leading-[1.75] text-white/85 whitespace-pre-wrap">{lesson.body}</p>
              <div className="mt-4 text-[12.5px] text-white/55 italic">…continues in the Academy.</div>
            </article>
            <div className="flex gap-2 mt-6">
              <button onClick={back} className="btn-ghost"><ArrowLeft className="w-4 h-4" /> Back</button>
              <button onClick={next} className="btn-primary" data-testid="tour-next-lesson">Nice. What else? <ArrowRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div data-testid="tour-step-agent">
            <div className="chip" style={{ color }}><Bot className="w-3.5 h-3.5 mr-1 inline" /> AI specialist preview</div>
            <h2 className="font-display text-[32px] md:text-[40px] mt-3">This is what coaching looks like.</h2>
            <p className="text-white/65 mt-1 text-[14px]">A real question someone asked {agent.agent}. The response below is what they actually got back.</p>
            <div className="card-shell p-5 mt-6 space-y-4">
              <div className="flex justify-end">
                <div className="max-w-[78%] rounded-2xl px-4 py-2.5 text-[14px] leading-relaxed whitespace-pre-wrap bg-[#ff2e63]/20 border border-[#ff2e63]/30">{agent.user_q}</div>
              </div>
              <div className="flex gap-3">
                <div className="w-9 h-9 rounded-full grid place-items-center shrink-0 text-[18px]" style={{ background: 'rgba(167,139,250,0.22)' }}>{agent.agent_emoji}</div>
                <div className="max-w-[78%]">
                  <div className="text-[11px] text-white/45 mb-1">{agent.agent}</div>
                  <div className="rounded-2xl px-4 py-2.5 text-[14px] leading-relaxed whitespace-pre-wrap bg-white/[0.04] border border-white/10">{agent.reply}</div>
                </div>
              </div>
            </div>
            <div className="text-[12px] text-white/45 mt-3">You'll unlock specialists by completing Academy tracks. First one is free.</div>
            <div className="flex gap-2 mt-6">
              <button onClick={back} className="btn-ghost"><ArrowLeft className="w-4 h-4" /> Back</button>
              <button onClick={next} className="btn-primary" data-testid="tour-next-agent">Keep going <ArrowRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}

        {step === 5 && (
          <div data-testid="tour-step-roadmap">
            <div className="chip" style={{ color }}><Calendar className="w-3.5 h-3.5 mr-1 inline" /> Your first 30 days</div>
            <h2 className="font-display text-[32px] md:text-[42px] mt-3">Here's what your first month <span className="brand-gradient">actually</span> looks like.</h2>
            <p className="text-white/65 mt-1 text-[14px]">Not marketing-speak. Just the concrete actions you'd take.</p>
            <div className="mt-6 space-y-3">
              {roadmap.map((r, i) => (
                <div key={i} className="card-shell p-4 flex items-start gap-4">
                  <div className="w-16 text-center">
                    <div className="text-[11px] uppercase tracking-wider text-white/50">{r.day}</div>
                  </div>
                  <div className="w-px bg-white/10 self-stretch" />
                  <div className="flex-1 text-[14px] leading-relaxed text-white/85">{r.what}</div>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-6">
              <button onClick={back} className="btn-ghost"><ArrowLeft className="w-4 h-4" /> Back</button>
              <button onClick={next} className="btn-primary" data-testid="tour-next-roadmap">Show me what I should pick <ArrowRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}

        {step === 6 && (
          <div data-testid="tour-step-interests">
            <div className="chip" style={{ color }}>Tailor your home</div>
            <h2 className="font-display text-[36px] md:text-[44px] mt-3">Pick what you actually want to see.</h2>
            <p className="text-white/60 mt-1 text-[14px]">We'll personalize your home page and your agent recommendations.</p>
            <div className="flex flex-wrap gap-2 mt-6 max-w-[720px]">
              {availableInterests.map(i => (
                <button key={i} onClick={() => setInterests(list => list.includes(i) ? list.filter(x => x !== i) : [...list, i])}
                  className={`chip ${interests.includes(i) ? 'active' : ''}`} data-testid={`tour-interest-${i.replace(/\s+/g, '-').toLowerCase()}`}>
                  {interests.includes(i) && <Check className="w-3 h-3 mr-1 inline" />} {i}
                </button>
              ))}
            </div>
            <div className="flex gap-2 mt-8">
              <button onClick={back} className="btn-ghost"><ArrowLeft className="w-4 h-4" /> Back</button>
              <button onClick={next} className="btn-primary" data-testid="tour-next-interests">Continue <ArrowRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}

        {step === 7 && (
          <div data-testid="tour-step-email">
            <div className="chip"><Shield className="w-3.5 h-3.5 mr-1 inline" /> No spam, ever</div>
            <h2 className="font-display text-[36px] md:text-[46px] mt-3">We'll save your progress.</h2>
            <p className="text-white/65 mt-2 text-[14.5px] max-w-[580px]">Drop an email so we can send you the signup link. You can still decide not to create an account yet — we won't pester you.</p>
            <input autoFocus type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@domain.com" className="input-field mt-6 max-w-[460px] text-[16px]" data-testid="tour-email-input" />
            <div className="flex gap-2 mt-6">
              <button onClick={back} className="btn-ghost"><ArrowLeft className="w-4 h-4" /> Back</button>
              <button onClick={() => { pingLead({ email, step_reached: 7 }); next(); }} disabled={!email.trim()} className="btn-primary disabled:opacity-50" data-testid="tour-next-email">Continue <ArrowRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}

        {step === 8 && (
          <div data-testid="tour-step-finish">
            <div className="chip active"><Flame className="w-3.5 h-3.5 mr-1 inline" /> Ready</div>
            <h2 className="font-display text-[38px] md:text-[52px] mt-3 leading-[1.02]">Alright <span className="brand-gradient">{name || 'friend'}</span>, you've seen it.</h2>
            <p className="text-white/65 mt-2 max-w-[580px] text-[15px]">Option 1: create the account now and pick up exactly where you left off. Option 2: we'll email you the signup link — come back when you're ready.</p>

            <div className="grid md:grid-cols-2 gap-4 mt-8 max-w-[820px]">
              <div className="card-shell p-5" style={{ borderColor: color + '55' }}>
                <div className="flex items-center gap-2" style={{ color }}><Rocket className="w-4 h-4" /><span className="uppercase text-[11px] tracking-widest font-bold">Jump in now</span></div>
                <h3 className="font-display text-[20px] mt-2">Create account &amp; enter</h3>
                <div className="text-[13px] text-white/65 mt-1">{email}</div>
                <input type="password" value={pwd} onChange={e => setPwd(e.target.value)} placeholder="Choose a password" className="input-field mt-3" data-testid="tour-password-input" />
                <button onClick={finishWithAccount} disabled={busy} className="btn-primary w-full justify-center mt-3 disabled:opacity-50" data-testid="tour-finish-account">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Create account
                </button>
                <div className="text-[11.5px] text-white/45 mt-2">Free forever · Includes VidCreForge video</div>
              </div>
              <div className="card-shell p-5">
                <div className="flex items-center gap-2 text-white/70"><MessageSquare className="w-4 h-4" /><span className="uppercase text-[11px] tracking-widest font-bold">Not ready yet</span></div>
                <h3 className="font-display text-[20px] mt-2">Email me the link</h3>
                <p className="text-[13px] text-white/65 mt-1">We'll save your tour progress and send a one-click signup link. No automated drip campaign.</p>
                <button onClick={finishAsLead} disabled={busy} className="btn-ghost w-full justify-center mt-4 disabled:opacity-50" data-testid="tour-finish-later">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Email me the signup link
                </button>
                <div className="text-[11.5px] text-white/45 mt-2">Unsubscribe anytime</div>
              </div>
            </div>

            <div className="flex items-center gap-3 mt-6 text-[12.5px] text-white/50 flex-wrap">
              <button onClick={back} className="btn-ghost text-[13px]"><ArrowLeft className="w-4 h-4" /> Back</button>
              <span>Step {step + 1} of {totalSteps}</span>
              <PwaInstallButton variant="ghost" label="Install the app" testid="tour-pwa-install" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
